import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  InfoCard,
  MarkdownContent,
  Progress,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { readmeApiRef } from '../../api';
import { Button, Typography } from '@material-ui/core';

type ReadmeCardProps = {
  title?: string;
  projectSlug?: string;
  gitRef?: string;
  variant?: string;
};

type State =
  | { status: 'idle' | 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'loaded'; content: string; sourceUrl?: string };

export const ReadmeCard = ({
  title = 'Repository README',
  projectSlug,
  gitRef,
}: ReadmeCardProps) => {
  const { entity } = useEntity();
  const readmeApi = useApi(readmeApiRef);
  const [state, setState] = useState<State>({ status: 'idle' });

  const effectiveSlug =
    projectSlug ?? entity.metadata.annotations?.['github.com/project-slug'];
  const entityRef = stringifyEntityRef(entity);

  useEffect(() => {
    let mounted = true;

    setState({ status: 'loading' });

    readmeApi
      .getReadme({
        entityRef,
        projectSlug: effectiveSlug,
        ref: gitRef,
      })
      .then(result => {
        if (!mounted) {
          return;
        }

        setState({
          status: 'loaded',
          content: result.content,
          sourceUrl: result.sourceUrl,
        });
      })
      .catch(error => {
        if (!mounted) {
          return;
        }

        setState({ status: 'error', error: error as Error });
      });

    return () => {
      mounted = false;
    };
  }, [entityRef, effectiveSlug, gitRef, readmeApi]);

  const action =
    state.status === 'loaded' && state.sourceUrl ? (
      <Button
        color="primary"
        variant="text"
        href={state.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        View in GitHub
      </Button>
    ) : undefined;

  return (
    <InfoCard title={title} action={action}>
      {state.status === 'loading' || state.status === 'idle' ? (
        <Progress />
      ) : null}

      {state.status === 'error' ? (
        <Typography color="error" variant="body2">
          {state.error.message}
        </Typography>
      ) : null}

      {state.status === 'loaded' ? (
        <MarkdownContent content={state.content} />
      ) : null}
    </InfoCard>
  );
};
